import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { useCallback } from 'react';

import type { Rarity, Sport } from '@sorare/core/src/__generated__/globalTypes';
import { CartItemState } from '@sorare/core/src/__generated__/globalTypes';
import { namedOperations } from '@sorare/core/src/__generated__/operations';
import { useCurrentUserContext } from '@sorare/core/src/contexts/currentUser';
import { myCartCardFragment } from '@sorare/core/src/contexts/currentUser/queries';
import { useMutation } from '@sorare/core/src/hooks/graphql/useMutation';
import { useRefetchQueries } from '@sorare/core/src/hooks/graphql/useRefetchQueries';
import { monetaryAmountFragment } from '@sorare/core/src/lib/monetaryAmount';

import { filterLivePromos } from '#src/lib/promotionalEvent.ts';

import type {
  AddToCartMutation,
  AddToCartMutationVariables,
  RefreshCartMutation,
  RefreshCartMutationVariables,
  RemoveFromCartMutation,
  RemoveFromCartMutationVariables,
  SaveForLaterMutation,
  SaveForLaterMutationVariables,
  useCart_anyCard,
  useCart_cart,
  useCart_cartItem,
} from './__generated__/useCart.graphql.ts';

const anyCardFragment = gql`
  fragment useCart_anyCard on AnyCardInterface {
    slug
    ...CurrentUserProvider_anyCard
  }
  ${myCartCardFragment}
` as TypedDocumentNode<useCart_anyCard>;

const cartItemFragment = gql`
  fragment useCart_cartItem on CartItem {
    id
    card {
      slug
      sport
      ...useCart_anyCard
    }
    state
    price {
      ...MonetaryAmountFragment_monetaryAmount
    }
  }
  ${anyCardFragment}
  ${monetaryAmountFragment}
` as TypedDocumentNode<useCart_cartItem>;

const cartFragment = gql`
  fragment useCart_cart on Cart {
    id
    sport
    price {
      ...MonetaryAmountFragment_monetaryAmount
    }
    mainCartItems {
      id
      ...useCart_cartItem
    }
    savedForLaterCartItems {
      id
      ...useCart_cartItem
    }
    affectedCardCollections {
      id
      scoreIncrease
      bonusIncrease
    }
    activePromotionalEvents {
      slug
      endDate
      startDate
      sport
      eligibilityRules {
        id
        eligibleCollections {
          cartCardsCount
          cardCollections {
            slug
            rarity
            anyTeam {
              slug
            }
            sportSeason {
              name
            }
          }
        }
        eligibleCartCardsCount
      }
    }
  }
  ${cartItemFragment}
  ${monetaryAmountFragment}
` as TypedDocumentNode<useCart_cart>;

const ADD_TO_CART_MUTATION = gql`
  mutation AddToCartMutation(
    $cardSlugs: [String!]
    $cartItemIds: [ID!]
    $sport: Sport!
    $withCollectionPredictions: Boolean = true
  ) {
    addToCart(
      input: { cardSlugs: $cardSlugs, sport: $sport, cartItemIds: $cartItemIds }
    ) {
      currentUser {
        slug
        myCart(sport: $sport) {
          id
          ...useCart_cart
        }
      }
    }
  }
  ${cartFragment}
` as TypedDocumentNode<AddToCartMutation, AddToCartMutationVariables>;

const REMOVE_FROM_CART_MUTATION = gql`
  mutation RemoveFromCartMutation(
    $removeFromCartInput: removeFromCartInput!
    $sport: Sport!
    $withCollectionPredictions: Boolean = true
  ) {
    removeFromCart(input: $removeFromCartInput) {
      currentUser {
        slug
        myCart(sport: $sport) {
          id
          ...useCart_cart
        }
      }
    }
  }
  ${cartFragment}
` as TypedDocumentNode<RemoveFromCartMutation, RemoveFromCartMutationVariables>;

const SAVE_FOR_LATER_MUTATION = gql`
  mutation SaveForLaterMutation(
    $saveForLaterInput: saveForLaterInput!
    $sport: Sport!
    $withCollectionPredictions: Boolean = true
  ) {
    saveForLater(input: $saveForLaterInput) {
      currentUser {
        slug
        myCart(sport: $sport) {
          id
          ...useCart_cart
        }
      }
    }
  }
  ${cartFragment}
` as TypedDocumentNode<SaveForLaterMutation, SaveForLaterMutationVariables>;

const REFRESH_CART_MUTATION = gql`
  mutation RefreshCartMutation(
    $refreshCartInput: refreshCartInput!
    $sport: Sport!
    $withCollectionPredictions: Boolean = true
  ) {
    refreshCart(input: $refreshCartInput) {
      currentUser {
        slug
        myCart(sport: $sport) {
          id
          ...useCart_cart
        }
      }
    }
  }
  ${cartFragment}
` as TypedDocumentNode<RefreshCartMutation, RefreshCartMutationVariables>;

export const useCart = () => {
  const { currentUser } = useCurrentUserContext();

  const { getRefetchQueries } = useRefetchQueries();
  const refetchQueries = getRefetchQueries([
    namedOperations.Query.LazyCartPromosQuery,
  ]);
  const [addToCartMutation, { loading: addToCartMutationLoading }] =
    useMutation(ADD_TO_CART_MUTATION, {
      refetchQueries,
    });
  const [removeFromCartMutation, { loading: removeFromCartMutationLoading }] =
    useMutation(REMOVE_FROM_CART_MUTATION, {
      refetchQueries,
    });
  const [saveForLaterMutation, { loading: saveForLaterMutationLoading }] =
    useMutation(SAVE_FOR_LATER_MUTATION, {
      refetchQueries,
    });
  const [refreshCartMutation, { loading: refreshCartMutationLoading }] =
    useMutation(REFRESH_CART_MUTATION, {
      refetchQueries,
    });

  const cart = currentUser?.myCart;

  const addCardsToCart = useCallback(
    async (sport: Sport, cardSlugs: string[]) => {
      const res = await addToCartMutation({
        variables: {
          sport,
          cardSlugs,
        },
      });
      return res.data?.addToCart?.currentUser?.myCart;
    },
    [addToCartMutation]
  );

  const addCartItemsToCart = useCallback(
    async (cartItemIds: string[], sport: Sport) => {
      const res = await addToCartMutation({
        variables: {
          cartItemIds,
          sport,
        },
      });
      return res.data?.addToCart?.currentUser?.myCart;
    },
    [addToCartMutation]
  );

  const removeFromCart = useCallback(
    async (cartItemIds: string[], sport: Sport) => {
      if (!cart) return null;
      const res = await removeFromCartMutation({
        variables: {
          removeFromCartInput: {
            cartId: cart?.id || '',
            cartItemIds,
          },
          sport,
        },
        optimisticResponse: {
          removeFromCart: {
            currentUser: {
              __typename: 'CurrentUser',
              slug: currentUser?.slug || '',
              myCart: {
                ...cart,
                id: cart?.id || '',
                mainCartItems: cart!.mainCartItems!.filter(
                  i => !cartItemIds.includes(i.id)
                ),
                savedForLaterCartItems: cart!.savedForLaterCartItems!.filter(
                  i => !cartItemIds.includes(i.id)
                ),
                __typename: 'Cart',
              },
            },
            __typename: 'removeFromCartPayload',
          },
        },
      });
      return res.data?.removeFromCart?.currentUser?.myCart;
    },
    [cart, removeFromCartMutation, currentUser]
  );

  const removeCardsFromCart = useCallback(
    async (cardSlugs: string[], sport: Sport) => {
      const cartItemIds =
        cart?.mainCartItems
          ?.filter(item => cardSlugs.includes(item.card.slug))
          .map(item => item.id) || [];
      await removeFromCart(cartItemIds, sport);
    },
    [removeFromCart, cart]
  );

  const saveForLater = useCallback(
    async (cartItemIds: string[], sport: Sport) => {
      if (!cart) return null;
      const res = await saveForLaterMutation({
        variables: {
          saveForLaterInput: {
            cartId: cart?.id || '',
            cartItemIds,
          },
          sport,
        },
      });
      return res.data?.saveForLater?.currentUser?.myCart;
    },
    [cart, saveForLaterMutation]
  );

  const refreshCart = useCallback(
    async (cartItemIds: string[], sport: Sport) => {
      if (!cart) return null;
      const res = await refreshCartMutation({
        variables: {
          refreshCartInput: {
            cartItemIds,
            cartId: cart?.id || '',
          },
          sport,
        },
      });
      return res.data?.refreshCart?.currentUser?.myCart;
    },
    [cart, refreshCartMutation]
  );

  const availableCartItems =
    cart?.mainCartItems?.filter(
      i => i.state && i.state !== CartItemState.REMOVED
    ) || [];

  const cartCount = availableCartItems?.length || 0;

  const removedCartCount =
    (cart?.mainCartItems?.length || 0) - (availableCartItems?.length || 0);

  const savedForLaterCount = cart?.savedForLaterCartItems?.length || 0;

  const cartTotal = cart?.price || undefined;
  const isInCart = ({ slug }: { slug: string }) =>
    currentUser?.myCart?.mainCartItems?.some(item => item.card.slug === slug);

  const similarCardisInCart = ({
    slug,
    rarityTyped,
    anyPlayer,
  }: {
    slug: string;
    rarityTyped: Rarity;
    anyPlayer: { slug: string };
  }) =>
    currentUser?.myCart?.mainCartItems?.some(
      item =>
        isInCart({ slug }) ||
        (item.card.rarityTyped === rarityTyped &&
          item.card.anyPlayer.slug === anyPlayer.slug)
    );

  const getCartItemByCard = ({
    slug,
    rarityTyped,
    anyPlayer,
  }: {
    slug: string;
    rarityTyped: Rarity;
    anyPlayer: { slug: string };
  }) =>
    currentUser?.myCart?.mainCartItems?.find(
      item =>
        item.card.slug === slug ||
        (item.card.rarityTyped === rarityTyped &&
          item.card.anyPlayer.slug === anyPlayer.slug)
    );

  const livePromos =
    (currentUser?.myCart &&
      filterLivePromos(
        cart?.activePromotionalEvents || [],
        currentUser.myCart.sport
      )) ||
    [];

  const hasLivePromos = !!livePromos?.length;

  return {
    addCardsToCart: {
      loading: addToCartMutationLoading,
      mutate: addCardsToCart,
    },
    addCartItemsToCart: {
      loading: addToCartMutationLoading,
      mutate: addCartItemsToCart,
    },
    removeFromCart: {
      loading: removeFromCartMutationLoading,
      mutate: removeFromCart,
    },
    removeCardsFromCart: {
      loading: removeFromCartMutationLoading,
      mutate: removeCardsFromCart,
    },
    saveForLater: {
      loading: saveForLaterMutationLoading,
      mutate: saveForLater,
    },
    refreshCart: {
      loading: refreshCartMutationLoading,
      mutate: refreshCart,
    },
    isInCart,
    similarCardisInCart,
    getCartItemByCard,
    cartCount,
    removedCartCount,
    savedForLaterCount,
    cart,
    cartTotal,
    livePromos,
    hasLivePromos,
  };
};

useCart.fragments = {
  anyCard: anyCardFragment,
  cartItem: cartItemFragment,
};
